/**
 * The credit vocabulary: what each ledger entry type means, and the answer to
 * the question a credit system gets asked most — "why was I charged for a
 * failure?" — said before anybody has to ask it.
 *
 * **CREDITS ARE WEND'S METERING UNIT, NOT MONEY.** Under BYO the tenant pays
 * AWS directly for what SES actually does, in amounts Wend never sees and
 * cannot compute — and from slice 7, Meta bills WhatsApp per 24-hour
 * conversation while Wend still counts one credit per message. So nothing in
 * this file, and nothing that reads it, may print "spend", "cost", "charge",
 * "price" or a currency symbol. It says **consumed** and **released**, because
 * — per `@wend/shared`'s own `credit.ts` — the vocabulary a file uses about
 * itself is the vocabulary that reaches the screen.
 *
 * **The one fact this file exists to put in front of a person before they
 * ask it:** a bounce, a rejection and a rendering failure all write
 * `release`, never `consume` — the reservation is undone and nothing is
 * consumed. Only a `delivery` ever writes `consume`. {@link ledgerEntrySentence}
 * says so in the `release` row's own words, and {@link ledgerBalanceMove} /
 * {@link ledgerReservedMove} are read straight out of `LEDGER_BALANCE_EFFECTS`
 * — imported from `@wend/shared`, never re-derived — so this screen cannot
 * show a `release` moving the spendable balance because two implementations
 * of the same arithmetic came to disagree.
 *
 * Three things imported rather than restated: `LEDGER_BALANCE_EFFECTS` (the
 * one arithmetic), `LEDGER_ENTRY_TYPE_VALUES` (the fixed enum, for a filter
 * control that must offer every member) and `availableCredits` (the only
 * supported way to turn a balance and a reservation into what a tenant can
 * still send). Nothing here recomputes any of the three.
 */
import {
  LEDGER_BALANCE_EFFECTS,
  LEDGER_ENTRY_TYPE_VALUES,
  type CreditLedgerEntry,
  type LedgerEntryType,
} from '@wend/shared';
import type { BadgeTone } from '@wend/ui';

/* -------------------------------------------------------------------------- */
/* Entry types                                                                 */
/* -------------------------------------------------------------------------- */

export interface LedgerEntryDisplay {
  readonly label: string;
  readonly tone: BadgeTone;
}

/**
 * One entry per member of the fixed `LedgerEntryType` enum, total — so a
 * seventh member added to CONTRACT.md fails this table at compile time rather
 * than rendering as a blank badge.
 *
 * `reserve`, `release` and `consume` are `info`/`neutral` rather than a
 * warning colour, even though `release` is the answer to "why wasn't I
 * charged": the tone a customer reads as trouble is reserved for trouble, and
 * a bounce being handled correctly is the opposite of that. `adjustment` is
 * the one `warn` — it is a human overriding the ledger by hand, and that is
 * always worth a second look, whichever way it moved.
 */
export const LEDGER_ENTRY_DISPLAY: Readonly<Record<LedgerEntryType, LedgerEntryDisplay>> = {
  grant: { label: 'Granted', tone: 'good' },
  reserve: { label: 'Reserved', tone: 'info' },
  release: { label: 'Released', tone: 'neutral' },
  consume: { label: 'Consumed', tone: 'neutral' },
  refund: { label: 'Refunded', tone: 'good' },
  adjustment: { label: 'Adjusted', tone: 'warn' },
};

/** Every type, for a filter control — including the two nothing writes yet. */
export const ALL_LEDGER_ENTRY_TYPES: readonly LedgerEntryType[] = LEDGER_ENTRY_TYPE_VALUES;

/**
 * Display order for the filter chips: the three this slice actually writes,
 * in the order a single message moves through them, then the two admin-only
 * types slice 8 introduces. `grant` and `adjustment` cannot appear in this
 * workspace's ledger yet — there is no admin console in this slice — and they
 * are still offered, the same way `ALL_SKIP_REASONS` offers a reason nobody is
 * carrying: a filter that can only name what already happened to exist is one
 * a person cannot use to confirm an absence.
 */
export const LEDGER_ENTRY_TYPE_ORDER: readonly LedgerEntryType[] = [
  'reserve',
  'consume',
  'release',
  'refund',
  'grant',
  'adjustment',
];

/**
 * The full sentence beside a row — what happened, in words a person who has
 * never seen a ledger can act on without reading a second screen.
 *
 * `adjustment` is the one type whose sentence depends on the entry rather than
 * only on the type, because it is the one type CONTRACT.md lets go negative —
 * see `LEDGER_BALANCE_EFFECTS` in `@wend/shared`.
 */
export function ledgerEntrySentence(entry: Pick<CreditLedgerEntry, 'type' | 'amount'>): string {
  switch (entry.type) {
    case 'grant':
      return 'A top-up added credits to this workspace.';
    case 'reserve':
      return 'Held back the moment a message was handed to the provider — until it is delivered or fails.';
    case 'release':
      return 'Given back: nothing was delivered, so nothing was consumed.';
    case 'consume':
      return 'Consumed because the message was delivered.';
    case 'refund':
      return 'A previously consumed credit was reversed.';
    case 'adjustment':
      return entry.amount < 0
        ? 'A manual correction removed credits from this workspace.'
        : 'A manual correction added credits to this workspace.';
  }
}

/* -------------------------------------------------------------------------- */
/* What an entry does to the two numbers                                      */
/* -------------------------------------------------------------------------- */

export type BalanceMoveDisplay = { readonly sign: '+' | '−'; readonly magnitude: number } | null;

/**
 * How this entry moved the **spendable balance** — `null` for `reserve` and
 * `release`, which move `reserved` and leave `balance` untouched. That is not
 * an omission here: it is the fact `LEDGER_BALANCE_EFFECTS` already carries,
 * read rather than re-derived, and it is exactly what stops this screen ever
 * showing a bounce's `release` entry as though it had consumed something.
 */
export function ledgerBalanceMove(
  entry: Pick<CreditLedgerEntry, 'type' | 'amount'>,
): BalanceMoveDisplay {
  const signedUnits = LEDGER_BALANCE_EFFECTS[entry.type].balance * entry.amount;
  if (signedUnits === 0) return null;
  return { sign: signedUnits > 0 ? '+' : '−', magnitude: Math.abs(signedUnits) };
}

/**
 * The same reading for `reserved` — what a `reserve` holds back and a
 * `release` (or a `consume`) undoes.
 */
export function ledgerReservedMove(
  entry: Pick<CreditLedgerEntry, 'type' | 'amount'>,
): BalanceMoveDisplay {
  const signedUnits = LEDGER_BALANCE_EFFECTS[entry.type].reserved * entry.amount;
  if (signedUnits === 0) return null;
  return { sign: signedUnits > 0 ? '+' : '−', magnitude: Math.abs(signedUnits) };
}
